﻿import { useEffect, useRef } from "react";

import { formatNumber } from "@/utils/numberFormat";

export default function usePhytosanitaryProgramFilters({
	lookups,
	appliedFilterParams,
	filtersState,
	setFilterOptions,
	handleFilterChange,
	handleApplyFilters,
	handleClearOneFilter,
	location,
}) {
	const isUrlSyncedRef = useRef(false);
	const previousSpeciesIdRef = useRef(null);
	const appliedFilterParamsRef = useRef(appliedFilterParams);
	const handleFilterChangeRef = useRef(handleFilterChange);
	const setFilterOptionsRef = useRef(setFilterOptions);
	const handleClearOneFilterRef = useRef(handleClearOneFilter);
	const handleApplyFiltersRef = useRef(handleApplyFilters);

	useEffect(() => {
		handleFilterChangeRef.current = handleFilterChange;
	});
	useEffect(() => {
		setFilterOptionsRef.current = setFilterOptions;
	});
	useEffect(() => {
		handleClearOneFilterRef.current = handleClearOneFilter;
	});
	useEffect(() => {
		handleApplyFiltersRef.current = handleApplyFilters;
	});
	useEffect(() => {
		appliedFilterParamsRef.current = appliedFilterParams;
	});

	useEffect(() => {
		if (!lookups?.crops || lookups.crops.length === 0) return;
		const currentSpecie = appliedFilterParamsRef.current?.specie_selector;
		if (currentSpecie === undefined || currentSpecie === null || currentSpecie === "") {
			handleFilterChangeRef.current("specie_selector", lookups.crops[0].value);
			setTimeout(() => {
				handleApplyFiltersRef.current();
			}, 100);
		}
	}, [lookups]);

	useEffect(() => {
		if (!lookups) return;

		const speciesOptions = (lookups.crops || []).map((crop) => ({
			value: crop.value,
			label: crop.subtitle ? `${crop.label} • ${formatNumber(crop.subtitle)} Ha` : crop.label,
		}));

		setFilterOptionsRef.current("specie_selector", speciesOptions, false);
		setFilterOptionsRef.current("phenological_state", lookups.phenological_states || []);
		setFilterOptionsRef.current("objective", lookups.objectives || []);
		setFilterOptionsRef.current("dealer_id", lookups.dealers || []);
		setFilterOptionsRef.current("type", lookups.application_types || []);
	}, [lookups]);

	useEffect(() => {
		if (!lookups) return;

		const currentSpecieId = appliedFilterParams.specie_selector;
		const hasSpecificSpecies = currentSpecieId !== "" && currentSpecieId !== null && currentSpecieId !== undefined && Number(currentSpecieId) !== 0;

		if (Array.isArray(lookups.crop_details)) {
			const filteredVarieties = hasSpecificSpecies
				? lookups.crop_details.filter((detail) => String(detail.crop_customer_id) === String(currentSpecieId))
				: [];

			setFilterOptionsRef.current(
				"variety",
				filteredVarieties.map((d) => ({
					value: d.value,
					label: d.label,
					crop_customer_id: d.crop_customer_id,
				})),
				false,
			);
		}

		if (Array.isArray(lookups.phytosanitary_programs)) {
			const programs = hasSpecificSpecies
				? lookups.phytosanitary_programs.filter((program) => String(program.crop_customer_id) === String(currentSpecieId))
				: lookups.phytosanitary_programs;

			setFilterOptionsRef.current(
				"program",
				programs.map((p) => ({
					value: p.value,
					label: p.label,
					crop_customer_id: p.crop_customer_id,
				})),
				false,
			);
		}
	}, [lookups, appliedFilterParams.specie_selector]);

	useEffect(() => {
		if (previousSpeciesIdRef.current === null) {
			previousSpeciesIdRef.current = appliedFilterParams.specie_selector;
			return;
		}

		if (previousSpeciesIdRef.current !== appliedFilterParams.specie_selector) {
			const filtersToClean = ["variety", "program"];

			filtersToClean.forEach((filterKey) => {
				const filter = filtersState.find((f) => f.key === filterKey);
				if (filter?.value !== "" && filter?.value !== null && filter?.value !== undefined) {
					handleClearOneFilterRef.current(filterKey);
				}
			});

			previousSpeciesIdRef.current = appliedFilterParams.specie_selector;
		}
	}, [appliedFilterParams.specie_selector, filtersState]);

	useEffect(() => {
		if (!lookups || isUrlSyncedRef.current) return;

		const searchParams = new URLSearchParams(location.search);
		const urlFilterMapping = {
			variety: "variety",
			phenological_state: "phenological_state",
			objective: "objective",
			dealer_id: "dealer_id",
			program: "program",
			type: "type",
		};

		let hasUrlParams = false;

		Object.entries(urlFilterMapping).forEach(([urlParam, filterKey]) => {
			if (searchParams.has(urlParam)) {
				const urlValue = searchParams.get(urlParam);
				if (urlValue !== null && urlValue !== "") {
					const parsedNumericValue = Number(urlValue);
					const parsedValue = Number.isNaN(parsedNumericValue) ? urlValue : parsedNumericValue;
					handleFilterChangeRef.current(filterKey, parsedValue);
					hasUrlParams = true;
				}
			}
		});

		if (hasUrlParams) {
			setTimeout(() => {
				handleApplyFiltersRef.current();
			}, 100);
		}

		isUrlSyncedRef.current = true;
	}, [lookups, location.search]);
}
